import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LogoFull } from "@/components/Logo";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  React.useEffect(() => { document.title = "Masuk | Jastip Manager"; }, []);
  React.useEffect(() => { if (user) navigate("/"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Berhasil masuk");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal masuk");
    } finally { setLoading(false); }
  };

  return (
    <div className="app-shell-bg min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <LogoFull className="h-16 mb-3" />
          <h1 className="font-heading text-2xl font-semibold">Jastip Australia Manager</h1>
          <p className="text-sm text-muted-foreground mt-1">Masuk untuk mengelola bisnis jastip Anda</p>
        </div>
        <form onSubmit={submit} className="rounded-2xl border border-border bg-card p-6 shadow-sm space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Email</label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nama@email.com" data-testid="login-email" required />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Password</label>
            <div className="relative">
              <Input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} className="pr-10" data-testid="login-password" required />
              <button type="button" onClick={() => setShowPassword((v) => !v)} data-testid="login-toggle-password"
                aria-label={showPassword ? "Sembunyikan password" : "Tampilkan password"}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1">
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <Button type="submit" className="w-full h-11" disabled={loading} data-testid="login-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Masuk"}
          </Button>
          <div className="text-center">
            <button type="button" onClick={() => setForgotOpen(true)} className="text-xs text-primary hover:underline" data-testid="login-forgot">
              Lupa password?
            </button>
          </div>
        </form>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Lupa Password</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>Untuk alasan keamanan, reset password dilakukan oleh Admin.</p>
            <p>Silakan hubungi Admin akun Anda untuk melakukan <b>Reset Password</b> melalui menu Pengaturan &rarr; Staff.</p>
            <p>Setelah password direset, Anda dapat mengubahnya sendiri melalui menu <b>Ubah Password</b> di profil akun.</p>
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setForgotOpen(false)} data-testid="forgot-close">Mengerti</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
